import React, { useMemo, useRef, useState } from 'react';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import {
  buildProductCsv,
  buildProductXls,
  buildProductTemplateCsv,
  downloadFile,
  parseProductImport,
  ImportRow,
  PRODUCT_EXCEL_HEADERS,
} from '../services/excel';
import '../pages/Sections.css';
import '../pages/ExcelData.css';

// Shared Product Master import/export actions: Export CSV / Export Excel /
// Import with a NEW-vs-UPDATE preview modal. Rows are matched to existing
// products by name (Android "Product Details" layout + Sub Category — see
// services/excel.ts). Used on the Products page toolbar and the Excel
// Export & Import screen.

interface Preview {
  rows: ImportRow[];
  newCount: number;
  updateCount: number;
  fileName: string;
}

interface Props {
  exportClass?: string; // button class for the two export buttons
  importClass?: string; // button class for the import button
}

const ProductImportExport: React.FC<Props> = ({ exportClass = 'btn-grey', importClass = 'btn-teal' }) => {
  const { products, addProduct, updateProduct } = usePos();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  const existingByName = useMemo(() => {
    const m = new Map<string, ReturnType<typeof products.find>>();
    products.forEach((p) => m.set(p.name.trim().toLowerCase(), p));
    return m;
  }, [products]);

  const stamp = () => new Date().toISOString().slice(0, 10);

  const exportCsv = () =>
    downloadFile(`Product-Details-${stamp()}.csv`, buildProductCsv(products), 'text/csv;charset=utf-8');

  const exportXls = () =>
    downloadFile(`Product-Details-${stamp()}.xls`, buildProductXls(products), 'application/vnd.ms-excel');

  const exportTemplate = () =>
    downloadFile('Product-Import-Template.csv', buildProductTemplateCsv(), 'text/csv;charset=utf-8');

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseProductImport(String(reader.result));
        if (rows.length === 0) {
          alert('No product rows found. Expecting the Product Details layout (Name, Category, Sub Category, …).');
        } else {
          let upd = 0;
          rows.forEach((r) => existingByName.has(r.name.trim().toLowerCase()) && upd++);
          setPreview({ rows, newCount: rows.length - upd, updateCount: upd, fileName: file.name });
        }
      } catch {
        alert('Could not read that file. Use a CSV or Excel (.xls) export from this screen.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const applyImport = async () => {
    if (!preview) return;
    preview.rows.forEach((r) => {
      const existing = existingByName.get(r.name.trim().toLowerCase());
      if (existing) {
        updateProduct({
          ...existing,
          regionalName: r.regionalName || existing.regionalName,
          price: r.price,
          category: r.category || existing.category,
          subCategory: r.subCategory || existing.subCategory,
          stockable: r.stockable,
          weighable: r.weighable,
          stock: r.stock,
          taxRatePercent: r.taxRatePercent ?? existing.taxRatePercent,
        });
      } else {
        addProduct({
          name: r.name,
          regionalName: r.regionalName,
          price: r.price,
          category: r.category,
          subCategory: r.subCategory || undefined,
          stockable: r.stockable,
          weighable: r.weighable,
          stock: r.stock,
          taxRatePercent: r.taxRatePercent,
        });
      }
    });
    db.logEvent({
      type: 'edit',
      detail: `Imported ${preview.rows.length} products from ${preview.fileName} (${preview.newCount} new, ${preview.updateCount} updated)`,
      userName: (await db.getCurrentUser())?.name,
    });
    setPreview(null);
  };

  return (
    <>
      <button className={exportClass} onClick={exportCsv} disabled={!products.length}>Export CSV</button>
      <button className={exportClass} onClick={exportXls} disabled={!products.length}>Export Excel</button>
      <button
        className={exportClass}
        onClick={exportTemplate}
        title={`Blank import structure with sample rows — columns: ${PRODUCT_EXCEL_HEADERS.join(' · ')}`}
      >
        Template
      </button>
      <button className={importClass} onClick={() => fileRef.current?.click()}>Import</button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xls,.xlsx,.txt,text/csv"
        style={{ display: 'none' }}
        onChange={onFile}
      />

      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="section-card xl-preview" onClick={(e) => e.stopPropagation()}>
            <div className="xl-card-head">
              <h2 style={{ margin: 0 }}>Import Preview · {preview.fileName}</h2>
              <button className="btn-grey" onClick={() => setPreview(null)}>✕</button>
            </div>
            <p className="muted">
              {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'} ·{' '}
              <strong>{preview.newCount}</strong> new · <strong>{preview.updateCount}</strong> update
            </p>
            <div className="xl-preview-scroll">
              <table className="section-table">
                <thead>
                  <tr>
                    <th></th><th>Name</th><th>Category</th><th>Sub Category</th><th className="right">Price</th>
                    <th className="right">Stock</th><th className="right">Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const isNew = !existingByName.has(r.name.trim().toLowerCase());
                    return (
                      <tr key={i}>
                        <td>
                          <span className={`xl-badge ${isNew ? 'new' : 'upd'}`}>{isNew ? 'NEW' : 'UPD'}</span>
                        </td>
                        <td>{r.name}</td>
                        <td>{r.category || '—'}</td>
                        <td>{r.subCategory || '—'}</td>
                        <td className="right">{r.price}</td>
                        <td className="right">{r.stock}</td>
                        <td className="right">{r.taxRatePercent != null ? `${r.taxRatePercent}%` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="xl-preview-actions">
              <button className="btn-red" onClick={() => setPreview(null)}>Cancel</button>
              <button className="btn-teal" onClick={applyImport}>Apply Import</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductImportExport;
